import { CreditCard, TrendingUp } from "lucide-react";
import CreditLimitCardSkeleton from "./CreditLimitCardSkelton";
import checkIfUserHasAccess from "../common/UserCheckAccess";

const CreditLimitCard = ({ totals, accountData, documentData }) => {
  const creditLimit = Number(accountData?.climit) || 0;
  const dr = Number(accountData?.debit ?? accountData?.dr ?? 0);
  const cr = Number(accountData?.credit ?? accountData?.cr ?? 0);
  const balance = dr - cr; // outstanding balance

  const usedLimit = balance;
  const availableLimit = creditLimit - balance;

  const usagePercentage = creditLimit > 0 ? (usedLimit / creditLimit) * 100 : 0;
  const availablePercentage =
    creditLimit > 0 ? (availableLimit / creditLimit) * 100 : 0;

  if (!accountData) {
    return <CreditLimitCardSkeleton />;
  }

  return (
    <div className="space-y-3 bg-gradient-to-br from-blue-50 to-blue-100 p-4 border-l-4 border-blue-600 rounded-xl text-blue-900 shadow-sm">
      <div className="flex items-center justify-between text-sm mb-2">
        <div className="flex items-center gap-2">
          {accountData?.CREDITMETHOD === "BLOCKED" ? (
            <p className="p-1 rounded-full bg-red-600 font-medium text-white px-3 text-xs">
              A/C Status: Blocked
            </p>
          ) : accountData?.CREDITMETHOD === "CREDIT BLOCK" ||
            accountData?.CREDITMETHOD === "Credit Block" ? (
            <p className="p-1 rounded-full bg-yellow-500 font-medium text-white px-3 text-xs">
              A/C Status: Credit Block
            </p>
          ) : (
            <p className="p-1 rounded-full bg-green-500 font-medium text-white px-3 text-xs">
              A/C Status: Active
            </p>
          )}
        </div>

        <div className="flex items-center gap-2">
          <p className="p-1 rounded-full bg-orange-500 font-medium text-white px-3 text-xs">
            Due In: {accountData?.ddays || accountData.duedays} Days{" "}
          </p>
        </div>
      </div>

      <div className="bg-gradient-to-r from-indigo-500 to-purple-600 p-3 rounded-lg shadow-lg mb-3">
        <div className="flex items-center justify-center">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-1">
              <CreditCard className="w-5 h-5 text-white" />
              <span className="text-sm font-medium text-white/90">
                Net Amount
              </span>
            </div>
            <span className="text-xl font-bold text-white">
              {documentData?.currency || ""} {totals.netAmount}
            </span>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-blue-700" />
            <span className="text-sm font-medium text-blue-800">
              Credit Limit
            </span>
          </div>
          <span className="text-lg font-bold text-blue-900">
            {documentData?.currency || ""} {creditLimit?.toLocaleString()}
          </span>
        </div>
        {creditLimit > 0 && (
          <div className="space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-600">
                Used: {documentData?.currency || ""}{" "}
                {usedLimit?.toLocaleString()}
              </span>
              <span className="text-green-600 font-medium">
                Available: {documentData?.currency || ""}{" "}
                {availableLimit?.toLocaleString()}
              </span>
            </div>

            <div className="w-full bg-gray-300 rounded-full h-3 overflow-hidden shadow-inner">
              <div
                className={`h-full rounded-full transition-all duration-500 ease-out ${usagePercentage > 80
                    ? "bg-gradient-to-r from-red-500 to-red-600"
                    : usagePercentage > 60
                      ? "bg-gradient-to-r from-yellow-400 to-yellow-500"
                      : "bg-gradient-to-r from-green-400 to-green-500"
                  }`}
                style={{ width: `${usagePercentage}%` }}
              ></div>
            </div>

            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-500 font-medium">
                {usagePercentage.toFixed(1)}% utilized
              </span>
              <div className="flex items-center gap-1">
                <TrendingUp className="w-3 h-3 text-green-600" />
                <span className="text-green-600 font-medium">
                  {((availableLimit / creditLimit) * 100).toFixed(1)}% available
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
      {checkIfUserHasAccess("JG") && (
        <div className="flex justify-between text-xs text-blue-800">
          <span className="font-medium">PDC Inhand</span>
          <span className="font-semibold">
            {accountData?.pdc?.toLocaleString() || "0"}
          </span>
        </div>
      )}
    </div>
  );
};

export default CreditLimitCard;
